import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import API_URL from '../config';

// ─── Styled ───────────────────────────────────────────────────────────────────

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.75rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04);
  margin-bottom: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.6rem;
  border-radius: 20px;
  background: #e0e7ff;
  color: #4f46e5;
  font-size: 12px;
  font-weight: 600;
  margin-left: 0.5rem;
`;

const RefreshBtn = styled.button`
  padding: 0.5rem 1rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #475569;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 0.4rem;

  &:hover:not(:disabled) {
    border-color: #4f46e5;
    color: #4f46e5;
    background: #eef2ff;
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  display: flex;
  align-items: center;
`;

const StatusText = styled.p`
  font-size: 14px;
  color: #94a3b8;
  text-align: center;
  padding: 2.5rem 0;
  margin: 0;
`;

const ErrorText = styled.p`
  font-size: 14px;
  color: #ef4444;
  text-align: center;
  padding: 2rem 0;
  margin: 0;
`;

const SectionBlock = styled.div`
  margin-bottom: 2rem;
  &:last-child { margin-bottom: 0; }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.625rem;
  border-bottom: 1px solid #f1f5f9;
`;

const SectionName = styled.h3`
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.07em;
`;

const SectionCount = styled.span`
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 0.875rem;
`;

const BookCard = styled.div`
  border-radius: 10px;
  overflow: hidden;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: box-shadow 0.15s, transform 0.15s;
  cursor: default;

  &:hover {
    box-shadow: 0 4px 12px rgba(79,70,229,0.1);
    transform: translateY(-2px);
    border-color: #c7d2fe;
  }
`;

const CoverWrap = styled.div`
  width: 100%;
  aspect-ratio: 0.71;
  background: #e2e8f0;
  overflow: hidden;
  position: relative;
`;

const CoverImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CoverPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  font-size: 28px;
`;

const BookInfo = styled.div`
  padding: 0.6rem 0.7rem;
`;

const BookTitle = styled.p`
  margin: 0 0 0.2rem;
  font-size: 12px;
  font-weight: 500;
  color: #1e293b;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.35;
`;

const BookMeta = styled.p`
  margin: 0;
  font-size: 11px;
  color: #94a3b8;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelease(releaseName) {
  const [num, year] = releaseName.split('-');
  if (num && year) return `№${num} / ${year}`;
  return releaseName;
}

function imageUrl(path) {
  return `${API_URL}/api/magazine/image?path=${encodeURIComponent(path)}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

function MagazineList({ theme }) {
  const [magazines, setMagazines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMagazines = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/magazine/get-magazines?date=2000-01-01`);
      if (!res.ok) throw new Error(`Ошибка сервера: ${res.status}`);
      const data = await res.json();
      data.sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate));
      setMagazines(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMagazines(); }, [fetchMagazines]);

  const sections = [];
  const grouped = {};
  for (const mag of magazines) {
    if (!grouped[mag.section]) {
      grouped[mag.section] = [];
      sections.push(mag.section);
    }
    grouped[mag.section].push(mag);
  }

  return (
    <Card>
      <Header>
        <HeaderTitle>
          Журналы на сервере
          {!loading && magazines.length > 0 && (
            <CountBadge>{magazines.length}</CountBadge>
          )}
        </HeaderTitle>
        <RefreshBtn onClick={fetchMagazines} disabled={loading}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={loading ? {animation:'spin 1s linear infinite'} : {}}>
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
            <path d="M21 3v5h-5"/>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
            <path d="M8 16H3v5"/>
          </svg>
          {loading ? 'Загрузка…' : 'Обновить'}
        </RefreshBtn>
      </Header>

      {loading && <StatusText>Загрузка журналов…</StatusText>}
      {!loading && error && <ErrorText>⚠ {error}</ErrorText>}
      {!loading && !error && magazines.length === 0 && (
        <StatusText>Журналов пока нет</StatusText>
      )}

      {!loading && !error && sections.map(section => (
        <SectionBlock key={section}>
          <SectionHeader>
            <SectionName>{section}</SectionName>
            <SectionCount>{grouped[section].length} шт.</SectionCount>
          </SectionHeader>
          <Grid>
            {grouped[section].map(mag => (
              <MagazineCard key={mag.id} mag={mag} />
            ))}
          </Grid>
        </SectionBlock>
      ))}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </Card>
  );
}

function MagazineCard({ mag }) {
  const [imgError, setImgError] = useState(false);

  return (
    <BookCard>
      <CoverWrap>
        {!imgError ? (
          <CoverImg
            src={imageUrl(mag.imageURL)}
            alt={mag.displayName}
            onError={() => setImgError(true)}
          />
        ) : (
          <CoverPlaceholder>📄</CoverPlaceholder>
        )}
      </CoverWrap>
      <BookInfo>
        <BookTitle>{mag.displayName}</BookTitle>
        <BookMeta>{formatRelease(mag.releaseName)}</BookMeta>
      </BookInfo>
    </BookCard>
  );
}

export default MagazineList;
